import { Location } from '@angular/common';
import { HttpClient, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';

import { environment } from '@env';
import { ProximityRightEnum, ProximityUser } from '@models';
import * as Sentry from '@sentry/angular';
import { mapObject } from '@utils';
import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { MessageBusService, MessageTypeEnum } from './message-bus.service';

/** Key from acting-as id in session storage */
const ACTING_KEY = 'ProxActingId';

/** Classifies an EmailConflictInfo.kind. Mirrors server EmailConflictKind enum. */
export type EmailConflictKind = 'OrphanAspNet' | 'InactiveUser' | 'ActiveUser';

/**
 * Details about who currently owns an email when a save would collide. Returned by
 * AuthService.getEmailConflict. Powers EmailConflictDialogComponent.
 */
/** One Account/Supplier/Global membership the conflicting user holds. */
export interface EmailConflictLocation {
  /** 'AccountUser' | 'AccountAdmin' | 'SupplierAdmin' | 'GlobalAdmin' */
  membershipType: 'AccountUser' | 'AccountAdmin' | 'SupplierAdmin' | 'GlobalAdmin';
  /** Display name (Account.Name, Supplier.Name, or "Global"). */
  name: string;
  /** Parent entity id (AccountId or SupplierId). Null for GlobalAdmin. */
  parentId?: string | null;
  /** Membership-row id (AccountUserId / AccountAdminId / SupplierAdminId / GlobalAdminId). */
  membershipId: string;
}

export interface EmailConflictInfo {
  userId: string;
  username: string;
  email: string;
  kind: EmailConflictKind;
  kindLabel: string;
  fullName?: string | null;
  roleName?: string | null;
  activeStatus?: string | null;
  canReassign: boolean;
  reassignBlockedReason?: string | null;
  /** True when caller is allowed to see WHERE the conflicting user lives. GlobalAdmins always
   * see all locations; PAAs see them only if at least one membership is inside their own account.
   * When false, `locations` is empty and the dialog shows a "contact a Global Admin" fallback. */
  locationVisibleToCaller: boolean;
  locations: EmailConflictLocation[];
}

export interface EmailConflictResult {
  available: boolean;
  conflict?: EmailConflictInfo;
}

export interface EmailReassignmentResult {
  success: boolean;
  message?: string;
  previousOwnerKind?: EmailConflictKind;
}

export class AuthUser extends ProximityUser {
  accountId?: string;
  supplierId?: string;
  accountUserId?: string;
  accountAdminId?: string;
  supplierAdminId?: string;
  companyName?: string;
  actingAsUser?: AuthUser;

  sessionMinutes?: number;
  role?: Roles;
  rights?: ProximityRightEnum[];
  origRole?: Roles;
  inactive?: boolean;
  emailConfirmed?: boolean;

  /** Training Mode: server-computed; true when this user is a true PAA whose AccountAdmin row is locked. */
  isTrainingLocked?: boolean;
  /** Training Mode: moment the current unlock expires. Null/undefined when locked. */
  trainingUnlockedUntil?: Date;

  /** constructor to make class instance from data received in response */
  constructor(source: AuthUser) {
    super();
    for (const p in source) {
      this[p] = source[p];
    }
  }

  get isAccountAdmin(): boolean {
    return this.hasRole(
      Roles.AccountAdmin,
      Roles.PrincipalAccountAdmin,
      Roles.ApprovalAdmin,
      Roles.OrderAdmin,
      Roles.SupportAdmin,
      Roles.GlobalAdmin,
    );
  }

  get isGlobalAdmin(): boolean {
    return this.hasRole(Roles.GlobalAdmin);
  }

  /**
   * Training Mode: true when the current effective user is a locked PAA AND we are not
   * a GlobalAdmin acting-as. Use hasRole (not hasRight) — hasRight auto-returns true for
   * GlobalAdmins, which would defeat the check.
   */
  get isLockedForTraining(): boolean {
    // hasOrigRole, not hasRole: impersonating replaces this.role with the target's, so a locked PAA
    // acting as an AccountAdmin would otherwise read as "not a PAA" and come back unlocked.
    if (!this.hasOrigRole(Roles.PrincipalAccountAdmin)) return false;
    if (this.origRole === Roles.GlobalAdmin) return false; // GlobalAdmin acting-as is never locked
    return this.isTrainingLocked === true;
  }

  /** Return true if user has any of the given roles */
  hasRole(...role: string[]): boolean {
    return !!(this.role && role.includes(this.role));
  }

  /** Return true if user has any of the given roles, or did before impersonating */
  hasOrigRole(...role: string[]): boolean {
    return !!((this.role && role.includes(this.role)) || (this.origRole && role.includes(this.origRole)));
  }

  /** Return true if user has any of the given rights */
  hasRight(...rights: ProximityRightEnum[]): boolean {
    if (this.hasRole(Roles.GlobalAdmin)) {
      return true;
    }
    return !!(this.rights && this.rights.some((r) => rights.includes(r)));
  }
}

/** Known names of ProximityRoles.  There may be others. */
export enum Roles {
  GlobalAdmin = 'GlobalAdministrator',
  AccountAdmin = 'AccountAdministrator',
  PrincipalAccountAdmin = 'PrincipalAccountAdministrator',
  ApprovalAdmin = 'ApprovalAdministrator',
  OrderAdmin = 'OrderAdministrator',
  SupportAdmin = 'SupportAdministrator',
  SupplierAdmin = 'SupplierAdministrator',
  AccountUser = 'AccountUser',
}

/**
 * Authenticate user and get user profile data from server.
 * Note that cookies must be supported - @see ./auth-request-options.ts
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private _user?: AuthUser;
  private _displayName = '';

  constructor(
    private _http: HttpClient,
    private _router: Router,
    private _location: Location,
    private _toastr: ToastrService,
    private _idle: Idle,
    private _keepalive: Keepalive,
    private _messageBus: MessageBusService,
  ) {}

  getUser(): AuthUser | undefined {
    return this._user;
  }

  get userDisplayName(): string {
    if (!this._user) {
      this._displayName = '';
    } else if (!this._displayName) {
      this._displayName = this._user.firstName + ' ' + this._user.lastName;
    }
    return this._displayName;
  }

  /**
   * Suffix for system-generated JobOrderNotes / log entries that identifies the acting user.
   * Returns `${text} by ${fullName} *` (with salutation), or `${text} *` if unauthenticated.
   * The trailing ` *` is the codebase's marker for system-generated notes.
   */
  decorateSystemNote(text: string): string {
    const name = this._user?.fullName;
    return name ? `${text} by ${name} *` : `${text} *`;
  }

  async getLoggedInUser(): Promise<AuthUser | undefined> {
    const isLoggedIn = await this.isLoggedIn();
    return isLoggedIn ? this._user : undefined;
  }

  /** Login and populate _user by building AuthUser from server data */
  async login(userName: string, password: string, persist: boolean): Promise<boolean> {
    const url = environment.apiUrl + 'Login/login';
    const creds = { userName: userName, password: password, persist: persist };
    const res = await firstValueFrom(this._http.post(url, creds));

    // The password was accepted, but the session lives in a cookie. When the page and the API
    // are on different sites, a private window, Safari, or strict tracking protection drops that
    // cookie, and every later call fails with "Authorization Error" (32 Contact Us reports,
    // June-October 2026). Prove the cookie came back before treating the user as signed in.
    // Goes away when the app and the API share one domain after the move to Unitec's Azure.
    if (!(await this.sessionCookieWorks())) {
      throw {
        status: 401,
        error: 'Your password is correct, but your browser blocked the Proximity sign-in. ' +
          'This happens in a private (incognito) window, in Safari, or with strict tracking protection. ' +
          'Open https://proximity.ideablade.com in a normal window and sign in there.',
      };
    }

    this.setUser(res);
    if (this._user?.id) {
      this._messageBus.notify({ id: MessageTypeEnum.Login });
      this._messageBus.notify({ id: MessageTypeEnum.Notification });
      this.setKeepAlive();
      this.navigateAfterLogin();
    }
    return true;
  }

  /** Change current user password */
  async changePassword(oldPassword: string, newPassword: string): Promise<boolean> {
    const url = environment.apiUrl + 'Login/changePassword';
    const creds = { oldPassword, newPassword };
    const res = await firstValueFrom(this._http.post(url, creds));
    return res !== null;
  }

  /** Send email with code to reset user password */
  async sendForgotPassword(email: string): Promise<boolean> {
    const url = environment.apiUrl + 'Login/forgotPassword';
    const creds = { email };
    const res = await firstValueFrom(this._http.post(url, creds));
    return res !== null;
  }

  /** Send notification to Proximity administrators */
  async sendContactUs(body: any): Promise<boolean> {
    const url = environment.apiUrl + 'Login/contactUs';
    const res = await firstValueFrom(this._http.post(url, body));
    return res !== null;
  }

  /** Change user password if code matches */
  async resetPassword(email: string, resetCode: string, newPassword: string): Promise<boolean> {
    const url = environment.apiUrl + 'Login/resetPassword';
    const creds = { email, resetCode, newPassword };
    const res = await firstValueFrom(this._http.post(url, creds));
    return res !== null;
  }

  /** Send email so user can confirm */
  async sendConfirmEmail(emailToConfirm: string | undefined) {
    if (!this._user) {
      throw new Error('Not logged in.');
    }
    const url = environment.apiUrl + 'Login/SendConfirmationLink';
    const creds = { userId: this._user.id };
    if (emailToConfirm) {
      (creds as any).email = emailToConfirm;
    }
    const res = await firstValueFrom(this._http.post(url, null, { params: creds }));
    return res !== null;
  }

  /** Confirm user email if code matches */
  /**
   * Confirms the email address.
   *
   * On a FIRST confirmation (the onboarding invite, not a change-email link) the server also
   * returns a password-reset token and the user name. That lets the caller send the user
   * straight to the password form in the same visit, instead of logging them out and leaving
   * them to discover "Forgot Password" on their own.
   *
   * Both are absent when the email was already confirmed, or on the change-email path.
   */
  async confirmEmail(
    userId: string,
    code: string,
    changedEmail: string,
  ): Promise<{ alreadyConfirmed: boolean; resetCode?: string; userName?: string }> {
    const url = environment.apiUrl + 'Login/confirmEmail';
    const creds = { userId, code };
    if (changedEmail) {
      (creds as any).changedEmail = changedEmail;
    }
    const res = await firstValueFrom(
      this._http.get<{ alreadyConfirmed?: boolean; resetCode?: string; userName?: string } | null>(url, { params: creds }),
    );
    return {
      alreadyConfirmed: !!(res && res.alreadyConfirmed),
      resetCode: res?.resetCode,
      userName: res?.userName,
    };
  }

  /**
   * True iff the given username is currently NOT in use by any Identity user. This is the
   * authoritative check — same call Identity uses when rejecting a save — and catches
   * orphan AspNetUsers rows that a Breeze query against ProximityUsers cannot see.
   * Use this (not the Breeze-based checkIfIsUnique) when deciding whether to auto-bump
   * a username to the next sequenced suffix.
   */
  async isUsernameAvailable(username: string): Promise<boolean> {
    if (!username || !username.trim()) return false;
    const url = environment.apiUrl + 'Login/IsUsernameAvailable';
    const res = (await firstValueFrom(this._http.get(url, { params: { username: username.trim() } }))) as { available: boolean };
    return !!res?.available;
  }

  /**
   * True iff the given email is currently NOT in use by any Identity user other than
   * `excludingUserId` (pass the current user's id in edit mode so their own email is not
   * flagged against itself). Authoritative — same table that owns the unique EmailIndex
   * the server enforces — and catches orphan / drifted AspNetUsers rows that a Breeze
   * query against ProximityUsers cannot see.
   * Empty / whitespace emails are treated as available (an empty email is permitted).
   */
  async isEmailAvailable(email: string | null | undefined, excludingUserId?: string): Promise<boolean> {
    if (!email || !email.trim()) return true;
    const url = environment.apiUrl + 'Login/IsEmailAvailable';
    const params: any = { email: email.trim() };
    if (excludingUserId) params.excludingUserId = excludingUserId;
    const res = (await firstValueFrom(this._http.get(url, { params }))) as { available: boolean };
    return !!res?.available;
  }

  /**
   * Richer version of isEmailAvailable — when the email is taken, also returns who owns
   * it (username, fullName if there's a matching ProximityUser, role, status), what kind
   * of record is blocking (orphan / inactive user / active user), and whether the current
   * caller is authorized to reassign it. Used by EmailConflictDialogComponent.
   */
  async getEmailConflict(email: string | null | undefined, excludingUserId?: string): Promise<EmailConflictResult> {
    if (!email || !email.trim()) return { available: true };
    const url = environment.apiUrl + 'Login/GetEmailConflict';
    const params: any = { email: email.trim() };
    if (excludingUserId) params.excludingUserId = excludingUserId;
    const raw = (await firstValueFrom(this._http.get(url, { params }))) as any;
    if (!raw) return { available: true };
    // Server serializes in PascalCase (Breeze-compat config in Startup). Normalize the
    // conflict payload to camelCase so EmailConflictDialogComponent's bindings find the
    // values they expect.
    const available = raw.available ?? raw.Available ?? true;
    const c = raw.conflict ?? raw.Conflict;
    if (available || !c) return { available: true };
    const rawLocations: any[] = c.locations ?? c.Locations ?? [];
    const locations: EmailConflictLocation[] = rawLocations.map((l) => ({
      membershipType: (l.membershipType ?? l.MembershipType) as EmailConflictLocation['membershipType'],
      name: l.name ?? l.Name ?? '',
      parentId: l.parentId ?? l.ParentId ?? null,
      membershipId: l.membershipId ?? l.MembershipId,
    }));
    const conflict: EmailConflictInfo = {
      userId: c.userId ?? c.UserId,
      username: c.username ?? c.Username,
      email: c.email ?? c.Email,
      kind: (c.kind ?? c.Kind) as EmailConflictKind,
      kindLabel: c.kindLabel ?? c.KindLabel,
      fullName: c.fullName ?? c.FullName,
      roleName: c.roleName ?? c.RoleName,
      activeStatus: c.activeStatus ?? c.ActiveStatus,
      canReassign: c.canReassign ?? c.CanReassign ?? false,
      reassignBlockedReason: c.reassignBlockedReason ?? c.ReassignBlockedReason,
      locationVisibleToCaller: c.locationVisibleToCaller ?? c.LocationVisibleToCaller ?? false,
      locations,
    };
    return { available: false, conflict };
  }

  /**
   * Atomically reassigns an email from one user to another. Server enforces that only
   * Global Admins and Principal Account Admins can call this.
   */
  /**
   * No-conflict path of the Change-Email flow: writes a (presumed-available) email
   * directly to an existing user. Server re-checks availability so a race with the client
   * pre-check cannot slip a duplicate past the unique EmailIndex. Authorized to Global
   * and Principal Account Admins only.
   */
  async changeEmail(userId: string, email: string | null): Promise<EmailReassignmentResult> {
    const url = environment.apiUrl + 'Login/SetEmail';
    try {
      const raw = (await firstValueFrom(this._http.post(url, { userId, email }))) as any;
      if (!raw) return { success: false, message: 'No response from server.' };
      return {
        success: raw.success ?? raw.Success ?? false,
        message: raw.message ?? raw.Message,
      };
    } catch (e: any) {
      const msg = e?.error?.message ?? e?.error?.Message ?? e?.message ?? 'Email change failed.';
      return { success: false, message: msg };
    }
  }

  async reassignEmail(fromUserId: string, toUserId: string, email: string): Promise<EmailReassignmentResult> {
    const url = environment.apiUrl + 'Login/ReassignEmail';
    try {
      const raw = (await firstValueFrom(
        this._http.post(url, { fromUserId, toUserId, email }),
      )) as any;
      if (!raw) return { success: false, message: 'No response from server.' };
      return {
        success: raw.success ?? raw.Success ?? false,
        message: raw.message ?? raw.Message,
        previousOwnerKind: (raw.previousOwnerKind ?? raw.PreviousOwnerKind) as EmailConflictKind | undefined,
      };
    } catch (e: any) {
      const msg = e?.error?.message ?? e?.error?.Message ?? e?.message ?? 'Reassignment failed.';
      return { success: false, message: msg };
    }
  }

  private setUser(res: any) {
    const user = new AuthUser(mapObject<AuthUser>(<any>res));
    this._user = user;
    Sentry.setUser({ displayName: user.fullName, email: user.email, username: user.username });
  }

  navigateToLogin(url?: string) {
    const dest = this.getDestUrl(url);
    this._router.navigate(['/login'], { queryParams: { redirectUrl: dest } });
    setTimeout(() => location.reload(), 100);
  }

  getDestUrl(url?: string) {
    let dest = url || this._router.routerState.snapshot.root.queryParams['redirectUrl'] || this._router.url;
    if (dest === '/login' || dest.startsWith('/auth')) {
      dest = '/';
    }
    return dest;
  }

  private navigateAfterLogin() {
    const dest = this.getDestUrl();
    if (this._user) {
      this._router.navigateByUrl(dest, { replaceUrl: true });
    }
  }

  async logout() {
    this.clearImpersonation();
    this._router.navigateByUrl('/login');
    this._user = undefined;
    // this._idle.stop();
    // this._messageBus.notify({ id: 'logout' });
    const url = environment.apiUrl + 'Login/Logout';
    try {
      await firstValueFrom(this._http.get(url));
      this.navigateToLogin();
    } catch (e) {
      console.log('Unable to logout.');
      this.navigateToLogin();
    }
  }

  async isLoggedIn(): Promise<boolean> {
    if (this._user) {
      return true;
    }

    // first see if the user is still logged in on the server.
    try {
      const res = await this.getUserFromBackend();
      return true;
    } catch (e: any) {
      // log error if not a 401 (Unauthorized) or 405 (Method not allowed) error.
      if (e.status !== 401 && e.status !== 405) {
        console.log('login check received unknown error: ' + e.status);
      }
      return false;
    }
  }

  /** If not logged in, redirect to the login page.  Save the intended url for navigation. */
  async redirectIfNotLoggedIn(url?: string): Promise<boolean> {
    const isLoggedIn = await this.isLoggedIn();
    if (!isLoggedIn) {
      this.navigateToLogin(url);
    }
    return isLoggedIn;
  }

  /** Act as the given AccountUser */
  async impersonateUser(proximityUserId: string) {
    const user = this.getUser();
    if (!user || !user.hasOrigRole(Roles.GlobalAdmin, Roles.AccountAdmin, Roles.PrincipalAccountAdmin)) {
      return;
    }
    const newUser = await this.getUserById(proximityUserId);
    if (newUser) {
      this.clearImpersonation();
      user.accountAdminId = newUser.accountAdminId;
      user.accountId = newUser.accountId;
      user.accountUserId = newUser.accountUserId;
      user.actingAsUser = newUser;
      user.companyName = newUser.companyName;
      user.supplierAdminId = newUser.supplierAdminId;
      user.supplierId = newUser.supplierId;
      user.origRole = user.role;
      user.role = newUser.role;
      user.rights = newUser.rights;
      user.isTrainingLocked = newUser.isTrainingLocked;
      user.trainingUnlockedUntil = newUser.trainingUnlockedUntil;
      const rolename = _.startCase(newUser.role || 'Unknown');
      this.setActingAsDisplayName(user.actingAsUser, rolename);
      sessionStorage.setItem(ACTING_KEY, proximityUserId);
      this._messageBus.notify({ id: MessageTypeEnum.Notification });
    }
  }

  /** Undo impersonation.  Reload current user so data is correct. */
  stopImpersonation() {
    this.clearImpersonation();
    location.reload();
  }

  /** Remove impersonation data, so admin is only him/herself */
  private clearImpersonation() {
    sessionStorage.removeItem(ACTING_KEY);
    this._displayName = '';
    const user = this.getUser();
    if (!user) {
      return;
    }
    user.accountAdminId = undefined;
    user.accountId = undefined;
    user.accountUserId = undefined;
    user.actingAsUser = undefined;
    user.companyName = undefined;
    user.supplierAdminId = undefined;
    user.supplierId = undefined;
    user.origRole = undefined;
    user.isTrainingLocked = undefined;
    user.trainingUnlockedUntil = undefined;
  }

  private setActingAsDisplayName(asUser: ProximityUser, role: string) {
    const user = this._user as AuthUser;
    this._displayName = `${user.firstName} ${user.lastName} as ${asUser.firstName} ${asUser.lastName} (${role})`;
  }

  /** Populate _user by building AuthUser from server data */
  private async getUserFromBackend(): Promise<AuthUser | undefined> {
    const url = environment.apiUrl + 'Login/GetLoggedInUser';
    const res = await firstValueFrom(this._http.get(url));

    this.setUser(res);
    this.setKeepAlive();
    const actingId = sessionStorage.getItem(ACTING_KEY);
    if (actingId) {
      await this.impersonateUser(actingId);
    }
    return this._user;
  }

  /** Get a user from the server by ProximityUser Id */
  private async getUserById(userId: string): Promise<AuthUser | undefined> {
    const url = environment.apiUrl + 'Login/GetUserById';
    const res = await firstValueFrom(this._http.get(url, { params: { userId } }));

    const user = new AuthUser(mapObject<AuthUser>(<any>res));
    return user;
  }

  /** False only when the server answers 401 to an authorized call made right after sign-in.
   * Any other failure counts as "works", so a network blip never blocks a sign-in. */
  private async sessionCookieWorks(): Promise<boolean> {
    try {
      await firstValueFrom(this._http.get(environment.apiUrl + 'Login/KeepAlive'));
      return true;
    } catch (err: any) {
      return err?.status !== 401;
    }
  }

  /** Monitor the app for user activity and send session keepalive requests to the server */
  private setKeepAlive() {
    // See https://moribvndvs.github.io/ng2-idle/quickstart

    const idle = this._idle;
    const keepalive = this._keepalive;

    if (idle.isRunning() || !this._user) {
      return;
    }

    if (idle.getInterrupts() && idle.getInterrupts().length) {
      // already configured, just start
      idle.watch();
      return;
    }

    // sets an idle timeout equal to the server session timeout.
    idle.setIdle((this._user?.sessionMinutes || 60) * 60);
    // sets a timeout grace period.  After idle + timeout, the user will be considered timed out.
    idle.setTimeout(60);
    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    idle.onTimeout.subscribe(() => {
      this.logout();
    });
    idle.onTimeoutWarning.subscribe((countdown) => {
      if (countdown % 10 === 0) {
        this._toastr.warning('You will be logged out in ' + countdown + ' seconds.  Click to keep your session alive.', 'Timeout');
      }
    });
    idle.onIdleEnd.subscribe(() => {
      keepalive.ping();
    });

    // sets the ping interval - how often to ping the server to keep the server session alive
    keepalive.interval(300);

    // set the ping request
    const url = environment.apiUrl + 'Login/KeepAlive';
    const req = new HttpRequest('GET', url, { withCredentials: true });
    keepalive.request(req);

    // start idle watching
    idle.watch();
  }
}
